import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikErrors } from 'formik';
import { IonIcon } from '@ionic/react';
import {
  arrowDownOutline,
  arrowUpOutline,
  chevronDownOutline,
  chevronUpOutline,
  trashOutline,
} from 'ionicons/icons';
import { StorefrontCollectionLayout } from '../enums/storefront-collection-layout.enum';
import { IStorefrontCollectionFormValue } from '../interfaces/storefront-form.interface';
import StorefrontProductIdsInput from './StorefrontProductIdsInput';
import {
  STOREFRONT_COLLECTION_DESCRIPTION_MAX_LENGTH,
  STOREFRONT_COLLECTION_NAME_MAX_LENGTH,
} from '../constants';

const StorefrontCollectionCard = ({
  collection,
  index,
  total,
  errors,
  submitCount,
  productNames,
  handleChange,
  handleBlur,
  setFieldValue,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  collection: IStorefrontCollectionFormValue;
  index: number;
  total: number;
  errors: FormikErrors<IStorefrontCollectionFormValue>;
  submitCount: number;
  productNames: Record<string, string>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(collection.name === '');

  const hasErrors = Object.keys(errors).length !== 0;

  // A collapsed card would otherwise hide the reason a submit did not go through.
  useEffect(() => {
    if (submitCount !== 0 && hasErrors) {
      setExpanded(true);
    }
  }, [submitCount, hasErrors]);

  return (
    <div className="box p-4 dark:bg-white/5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="flex-1 flex items-center gap-2 text-left"
          onClick={() => setExpanded(!expanded)}
        >
          {hasErrors && (
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
          )}

          <span className="text-sm font-semibold line-clamp-1">
            {collection.name === ''
              ? t('Storefront.untitledCollection')
              : collection.name}
          </span>

          <span className="text-xs text-gray-500 shrink-0">
            {/*@ts-ignore*/}
            {t(`StorefrontCollectionLayout.${collection.layout}`)} ·{' '}
            {t('Storefront.productCount', {
              count: collection.productIds.length,
            })}
          </span>
        </button>

        <button
          type="button"
          className="button-icon"
          disabled={index === 0}
          aria-label={t('Storefront.moveUp')}
          data-uk-tooltip={`title: ${t('Storefront.moveUp')}; pos: top;`}
          onClick={onMoveUp}
        >
          <IonIcon icon={arrowUpOutline} className="text-lg" />
        </button>

        <button
          type="button"
          className="button-icon"
          disabled={index === total - 1}
          aria-label={t('Storefront.moveDown')}
          data-uk-tooltip={`title: ${t('Storefront.moveDown')}; pos: top;`}
          onClick={onMoveDown}
        >
          <IonIcon icon={arrowDownOutline} className="text-lg" />
        </button>

        <button
          type="button"
          className="button-icon text-red-500"
          aria-label={t('Storefront.removeCollection')}
          data-uk-tooltip={`title: ${t('Storefront.removeCollection')}; pos: top;`}
          onClick={onRemove}
        >
          <IonIcon icon={trashOutline} className="text-lg" />
        </button>

        <button
          type="button"
          className="button-icon"
          aria-label={t('Storefront.toggleCollection')}
          onClick={() => setExpanded(!expanded)}
        >
          <IonIcon
            icon={expanded ? chevronUpOutline : chevronDownOutline}
            className="text-lg"
          />
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 mt-4 pt-4 border-t dark:border-slate-700">
          <div>
            <div className="flex items-center justify-between">
              <label
                className="text-sm font-medium"
                htmlFor={`collections.${index}.name`}
              >
                {t('Storefront.collectionName')}
              </label>
              <span className="text-xs text-gray-500">
                {collection.name.length}/{STOREFRONT_COLLECTION_NAME_MAX_LENGTH}
              </span>
            </div>

            <div className="mt-2.5">
              <input
                id={`collections.${index}.name`}
                name={`collections.${index}.name`}
                type="text"
                className="w-full"
                value={collection.name}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              {errors.name && (
                <p className="text-red-500 text-xs italic mt-1.5">
                  {errors.name}
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                className="text-sm font-medium"
                htmlFor={`collections.${index}.description`}
              >
                {t('Storefront.collectionDescription')}
              </label>
              <span className="text-xs text-gray-500">
                {collection.description.length}/
                {STOREFRONT_COLLECTION_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>

            <div className="mt-2.5">
              <textarea
                id={`collections.${index}.description`}
                name={`collections.${index}.description`}
                rows={2}
                className="w-full text-sm"
                value={collection.description}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              {errors.description && (
                <p className="text-red-500 text-xs italic mt-1.5">
                  {errors.description}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              className="text-sm font-medium"
              htmlFor={`collections.${index}.layout`}
            >
              {t('Storefront.collectionLayout')}
            </label>

            <div className="mt-2.5">
              <select
                id={`collections.${index}.layout`}
                name={`collections.${index}.layout`}
                className="w-full sm:w-64 dark:bg-slate-800"
                value={collection.layout}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {Object.values(StorefrontCollectionLayout).map((layout) => (
                  <option key={layout} value={layout}>
                    {/*@ts-ignore*/}
                    {t(`StorefrontCollectionLayout.${layout}`)}
                  </option>
                ))}
              </select>

              {errors.layout && (
                <p className="text-red-500 text-xs italic mt-1.5">
                  {errors.layout}
                </p>
              )}
            </div>
          </div>

          <StorefrontProductIdsInput
            productIds={collection.productIds}
            productNames={productNames}
            provisionalNames={collection.provisionalNames}
            productUrls={collection.productUrls}
            onChange={(productIds) =>
              setFieldValue(`collections.${index}.productIds`, productIds)
            }
            onRememberLinks={(urls, names) =>
              setFieldValue(`collections.${index}`, {
                ...collection,
                productUrls: { ...collection.productUrls, ...urls },
                provisionalNames: {
                  ...collection.provisionalNames,
                  ...names,
                },
              })
            }
          />

          {typeof errors.productIds === 'string' && (
            <p className="text-red-500 text-xs italic mt-1.5">
              {errors.productIds}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StorefrontCollectionCard;
